import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Search,
  RefreshCw,
  Plus,
  Edit,
  Trash2,
  DollarSign,
  Package,
  Truck,
  CheckCircle,
  Clock,
  Building2,
  ChevronRight,
  Save,
  X,
  CreditCard,
  FileText,
  Globe,
  Phone,
  Mail,
  AlertCircle,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SupplierPurchases = () => {
  const { agent } = useAuth();
  const [activeTab, setActiveTab] = useState("orders");
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Suppliers state
  const [suppliers, setSuppliers] = useState([]);
  const [supplierDialog, setSupplierDialog] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);

  // Purchase Orders state
  const [orders, setOrders] = useState([]);
  const [orderStats, setOrderStats] = useState({});
  const [filters, setFilters] = useState({
    status: "all",
    payment_status: "all",
    supplier_id: "all",
  });
  const [orderDialog, setOrderDialog] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [orderItems, setOrderItems] = useState([{ product_name: "", sku: "", quantity: 1, unit_cost: 0 }]);

  // Payment dialog
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [paymentOrder, setPaymentOrder] = useState(null);
  const [paymentData, setPaymentData] = useState({ amount: 0, payment_method: "", reference_number: "", notes: "" });

  // Finance state
  const [payablesSummary, setPayablesSummary] = useState(null);

  // Supplier Catalog state
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [catalogStats, setCatalogStats] = useState({});
  const [catalogFilters, setCatalogFilters] = useState({ category: "all", stock_status: "all" });
  const [catalogSearch, setCatalogSearch] = useState("");
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    fetchSuppliers();
    fetchOrders();
    fetchOrderStats();
    fetchPayablesSummary();
    fetchCatalogProducts();
    fetchCatalogStats();
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [filters, searchQuery]);

  useEffect(() => {
    fetchCatalogProducts();
  }, [catalogFilters, catalogSearch]);

  // ============ CATALOG FUNCTIONS ============
  const fetchCatalogProducts = async () => {
    try {
      const params = new URLSearchParams();
      if (catalogFilters.category !== "all") params.append("category", catalogFilters.category);
      if (catalogFilters.stock_status !== "all") params.append("stock_status", catalogFilters.stock_status);
      if (catalogSearch) params.append("search", catalogSearch);
      params.append("limit", "100");

      const response = await axios.get(`${API}/supplier-products?${params.toString()}`);
      setCatalogProducts(response.data.products || []);
      if (response.data.categories) {
        setCatalogStats(prev => ({ ...prev, categories: response.data.categories }));
      }
    } catch (error) {
      console.error("Error fetching catalog:", error);
    }
  };

  const fetchCatalogStats = async () => {
    try {
      const response = await axios.get(`${API}/supplier-products/stats`);
      setCatalogStats(response.data || {});
    } catch (error) {
      console.error("Error fetching catalog stats:", error);
    }
  };

  const handleSyncProducts = async (supplierId) => {
    setSyncing(true);
    try {
      await axios.post(`${API}/suppliers/${supplierId}/sync-products`);
      toast.success("Product sync started! This may take a few minutes...");
      
      // Poll for updates every 10 seconds for 2 minutes
      let attempts = 0;
      const pollInterval = setInterval(async () => {
        attempts++;
        await fetchCatalogProducts();
        await fetchCatalogStats();
        
        if (attempts >= 12) { // 2 minutes
          clearInterval(pollInterval);
          setSyncing(false);
          toast.info("Sync complete! Refresh to see latest products.");
        }
      }, 10000);
      
      // Also set a timeout to stop syncing state
      setTimeout(() => {
        setSyncing(false);
      }, 30000);
      
    } catch (error) {
      console.error("Error syncing products:", error);
      toast.error("Failed to start product sync");
      setSyncing(false);
    }
  };

  // ============ SUPPLIER FUNCTIONS ============
  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API}/suppliers`);
      setSuppliers(response.data || []);
    } catch (error) {
      console.error("Error fetching suppliers:", error);
    }
  };

  const handleSaveSupplier = async () => {
    try {
      if (!editingSupplier?.name) {
        toast.error("Supplier name is required");
        return;
      }

      if (editingSupplier.id) {
        await axios.put(`${API}/suppliers/${editingSupplier.id}`, editingSupplier);
        toast.success("Supplier updated");
      } else {
        await axios.post(`${API}/suppliers`, editingSupplier);
        toast.success("Supplier created");
      }
      setSupplierDialog(false);
      setEditingSupplier(null);
      fetchSuppliers();
    } catch (error) {
      console.error("Error saving supplier:", error);
      toast.error(error.response?.data?.detail || "Failed to save supplier");
    }
  };

  const handleDeleteSupplier = async (supplierId) => {
    if (!confirm("Are you sure you want to delete this supplier?")) return;
    try {
      await axios.delete(`${API}/suppliers/${supplierId}`);
      toast.success("Supplier deleted");
      fetchSuppliers();
    } catch (error) {
      console.error("Error deleting supplier:", error);
      toast.error(error.response?.data?.detail || "Failed to delete supplier");
    }
  };

  // ============ PURCHASE ORDER FUNCTIONS ============
  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status !== "all") params.append("status", filters.status);
      if (filters.payment_status !== "all") params.append("payment_status", filters.payment_status);
      if (filters.supplier_id !== "all") params.append("supplier_id", filters.supplier_id);
      if (searchQuery) params.append("search", searchQuery);
      params.append("limit", "100");

      const response = await axios.get(`${API}/purchase-orders?${params.toString()}`);
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error("Error fetching orders:", error);
      toast.error("Failed to fetch purchase orders");
    } finally {
      setLoading(false);
    }
  };

  const fetchOrderStats = async () => {
    try {
      const response = await axios.get(`${API}/purchase-orders/stats`);
      setOrderStats(response.data || {});
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const fetchPayablesSummary = async () => {
    try {
      const response = await axios.get(`${API}/finance/payables-summary`);
      setPayablesSummary(response.data);
    } catch (error) {
      console.error("Error fetching payables:", error);
    }
  };

  const handleCreateOrder = () => {
    setEditingOrder({
      supplier_id: "",
      shipping_cost: 0,
      customs_duty: 0,
      other_charges: 0,
      currency: "USD",
      exchange_rate: 280, // Default PKR rate
      notes: "",
    });
    setOrderItems([{ product_name: "", sku: "", quantity: 1, unit_cost: 0 }]);
    setOrderDialog(true);
  };

  const handleEditOrder = (order) => {
    setEditingOrder({ ...order });
    setOrderItems(order.items?.length > 0 ? order.items : [{ product_name: "", sku: "", quantity: 1, unit_cost: 0 }]);
    setOrderDialog(true);
  };

  const addOrderItem = () => {
    setOrderItems([...orderItems, { product_name: "", sku: "", quantity: 1, unit_cost: 0 }]);
  };

  const removeOrderItem = (index) => {
    if (orderItems.length > 1) {
      setOrderItems(orderItems.filter((_, i) => i !== index));
    }
  };

  const updateOrderItem = (index, field, value) => {
    const updated = [...orderItems];
    updated[index] = { ...updated[index], [field]: value };
    setOrderItems(updated);
  };

  const calculateOrderTotal = () => {
    const subtotal = orderItems.reduce((sum, item) => sum + (item.quantity * item.unit_cost), 0);
    const total = subtotal + (parseFloat(editingOrder?.shipping_cost) || 0) + 
                  (parseFloat(editingOrder?.customs_duty) || 0) + 
                  (parseFloat(editingOrder?.other_charges) || 0);
    return { subtotal, total };
  };

  const handleSaveOrder = async () => {
    try {
      if (!editingOrder?.supplier_id) {
        toast.error("Please select a supplier");
        return;
      }

      const validItems = orderItems.filter(item => item.product_name);
      if (validItems.length === 0) {
        toast.error("Add at least one item");
        return;
      }

      const orderData = {
        ...editingOrder,
        items: validItems,
        created_by: agent?.username,
      };

      if (editingOrder.id) {
        await axios.put(`${API}/purchase-orders/${editingOrder.id}`, orderData);
        toast.success("Purchase order updated");
      } else {
        await axios.post(`${API}/purchase-orders`, orderData);
        toast.success("Purchase order created");
      }
      
      setOrderDialog(false);
      setEditingOrder(null);
      fetchOrders();
      fetchOrderStats();
    } catch (error) {
      console.error("Error saving order:", error);
      toast.error(error.response?.data?.detail || "Failed to save purchase order");
    }
  };

  const handleUpdateStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/purchase-orders/${orderId}/status?status=${newStatus}`);
      toast.success(`Status updated to ${newStatus}`);
      fetchOrders();
      fetchOrderStats();
      fetchPayablesSummary();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!confirm("Are you sure you want to delete this purchase order?")) return;
    try {
      await axios.delete(`${API}/purchase-orders/${orderId}`);
      toast.success("Purchase order deleted");
      fetchOrders();
      fetchOrderStats();
    } catch (error) {
      console.error("Error deleting order:", error);
      toast.error(error.response?.data?.detail || "Failed to delete order");
    }
  };

  // ============ PAYMENT FUNCTIONS ============
  const handleOpenPayment = (order) => {
    setPaymentOrder(order);
    setPaymentData({
      amount: order.total_amount - (order.amount_paid || 0),
      payment_method: "Bank Transfer",
      reference_number: "",
      notes: "",
    });
    setPaymentDialog(true);
  };

  const handleRecordPayment = async () => {
    try {
      if (paymentData.amount <= 0) {
        toast.error("Payment amount must be greater than 0");
        return;
      }

      await axios.post(`${API}/purchase-orders/${paymentOrder.id}/payments`, {
        ...paymentData,
        created_by: agent?.username,
      });
      
      toast.success("Payment recorded");
      setPaymentDialog(false);
      setPaymentOrder(null);
      fetchOrders();
      fetchOrderStats();
      fetchPayablesSummary();
    } catch (error) {
      console.error("Error recording payment:", error);
      toast.error("Failed to record payment");
    }
  };

  // ============ RENDER HELPERS ============
  const getStatusBadge = (status) => {
    const variants = {
      DRAFT: "bg-gray-100 text-gray-800",
      SUBMITTED: "bg-blue-100 text-blue-800",
      CONFIRMED: "bg-indigo-100 text-indigo-800",
      SHIPPED: "bg-purple-100 text-purple-800",
      RECEIVED: "bg-green-100 text-green-800",
      CANCELLED: "bg-red-100 text-red-800",
    };
    return <Badge className={`${variants[status] || "bg-gray-100 text-gray-800"} font-medium`}>{status}</Badge>;
  };

  const getPaymentBadge = (status) => {
    const variants = {
      UNPAID: "bg-red-100 text-red-800",
      PARTIAL: "bg-yellow-100 text-yellow-800",
      PAID: "bg-green-100 text-green-800",
    };
    return <Badge className={`${variants[status] || "bg-gray-100 text-gray-800"} font-medium`}>{status}</Badge>;
  };

  const { subtotal, total } = editingOrder ? calculateOrderTotal() : { subtotal: 0, total: 0 };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-8 py-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Supplier Purchases</h1>
            <p className="text-sm text-gray-500 mt-1">Manage suppliers, purchase orders, and payments</p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => { fetchOrders(); fetchSuppliers(); fetchOrderStats(); fetchPayablesSummary(); }} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-5 gap-4 mt-6">
          <Card className="border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase">Total Orders</p>
                  <p className="text-2xl font-bold text-gray-900">{orderStats.total_orders || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <DollarSign className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase">Total Amount</p>
                  <p className="text-2xl font-bold text-green-600">${(orderStats.total_amount || 0).toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <CreditCard className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase">Total Paid</p>
                  <p className="text-2xl font-bold text-purple-600">${(orderStats.total_paid || 0).toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase">Outstanding</p>
                  <p className="text-2xl font-bold text-red-600">${(orderStats.outstanding || 0).toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Building2 className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase">Suppliers</p>
                  <p className="text-2xl font-bold text-orange-600">{suppliers.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-8 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="orders" className="gap-2">
              <Package className="w-4 h-4" /> Purchase Orders
            </TabsTrigger>
            <TabsTrigger value="catalog" className="gap-2">
              <Globe className="w-4 h-4" /> Supplier Catalog
            </TabsTrigger>
            <TabsTrigger value="suppliers" className="gap-2">
              <Building2 className="w-4 h-4" /> Suppliers
            </TabsTrigger>
            <TabsTrigger value="payables" className="gap-2">
              <DollarSign className="w-4 h-4" /> Accounts Payable
            </TabsTrigger>
          </TabsList>

          {/* Purchase Orders Tab */}
          <TabsContent value="orders">
            <Card>
              <CardHeader className="border-b">
                <div className="flex items-center justify-between">
                  <CardTitle>Purchase Orders</CardTitle>
                  <Button onClick={handleCreateOrder} className="bg-green-600 hover:bg-green-700">
                    <Plus className="w-4 h-4 mr-2" /> New Purchase Order
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {/* Filters */}
                <div className="p-4 border-b bg-gray-50 flex items-center gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search by PO#, supplier..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <Select value={filters.supplier_id} onValueChange={(v) => setFilters({ ...filters, supplier_id: v })}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Supplier" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Suppliers</SelectItem>
                      {suppliers.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
                    <SelectTrigger className="w-36">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="DRAFT">Draft</SelectItem>
                      <SelectItem value="SUBMITTED">Submitted</SelectItem>
                      <SelectItem value="CONFIRMED">Confirmed</SelectItem>
                      <SelectItem value="SHIPPED">Shipped</SelectItem>
                      <SelectItem value="RECEIVED">Received</SelectItem>
                      <SelectItem value="CANCELLED">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={filters.payment_status} onValueChange={(v) => setFilters({ ...filters, payment_status: v })}>
                    <SelectTrigger className="w-36">
                      <SelectValue placeholder="Payment" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Payments</SelectItem>
                      <SelectItem value="UNPAID">Unpaid</SelectItem>
                      <SelectItem value="PARTIAL">Partial</SelectItem>
                      <SelectItem value="PAID">Paid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Orders Table */}
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>PO Number</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Supplier</TableHead>
                      <TableHead>Items</TableHead>
                      <TableHead>Total</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Payment</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-gray-500">Loading...</TableCell>
                      </TableRow>
                    ) : orders.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                          <Package className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                          <p>No purchase orders found</p>
                          <Button onClick={handleCreateOrder} variant="link" className="mt-2">Create your first order</Button>
                        </TableCell>
                      </TableRow>
                    ) : (
                      orders.map((order) => (
                        <TableRow key={order.id} className="hover:bg-gray-50">
                          <TableCell className="font-medium text-blue-600">{order.po_number}</TableCell>
                          <TableCell className="text-sm text-gray-600">
                            {new Date(order.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium text-gray-900">{order.supplier_name}</p>
                              {order.supplier_order_ref && (
                                <p className="text-xs text-gray-500">Ref: {order.supplier_order_ref}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{order.items?.length || 0} items</Badge>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-bold text-gray-900">${order.total_amount?.toLocaleString()}</p>
                              <p className="text-xs text-gray-500">PKR {order.total_amount_local?.toLocaleString()}</p>
                            </div>
                          </TableCell>
                          <TableCell>{getStatusBadge(order.status)}</TableCell>
                          <TableCell>
                            <div>
                              {getPaymentBadge(order.payment_status)}
                              {order.amount_paid > 0 && (
                                <p className="text-xs text-gray-500 mt-1">Paid: ${order.amount_paid?.toLocaleString()}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="sm" onClick={() => handleEditOrder(order)} title="Edit">
                                <Edit className="w-4 h-4" />
                              </Button>
                              {order.status !== "RECEIVED" && order.status !== "CANCELLED" && (
                                <Select 
                                  value=""
                                  onValueChange={(v) => handleUpdateStatus(order.id, v)}
                                >
                                  <SelectTrigger className="w-8 h-8 p-0 border-0">
                                    <ChevronRight className="w-4 h-4" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="SUBMITTED">Mark Submitted</SelectItem>
                                    <SelectItem value="CONFIRMED">Mark Confirmed</SelectItem>
                                    <SelectItem value="SHIPPED">Mark Shipped</SelectItem>
                                    <SelectItem value="RECEIVED">Mark Received</SelectItem>
                                    <SelectItem value="CANCELLED">Cancel</SelectItem>
                                  </SelectContent>
                                </Select>
                              )}
                              {order.payment_status !== "PAID" && (
                                <Button variant="ghost" size="sm" onClick={() => handleOpenPayment(order)} title="Record Payment">
                                  <CreditCard className="w-4 h-4 text-green-600" />
                                </Button>
                              )}
                              {(order.status === "DRAFT" || order.status === "CANCELLED") && (
                                <Button variant="ghost" size="sm" onClick={() => handleDeleteOrder(order.id)} title="Delete">
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Supplier Catalog Tab */}
          <TabsContent value="catalog">
            <Card>
              <CardHeader className="border-b">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Supplier Product Catalog</CardTitle>
                    <p className="text-sm text-gray-500 mt-1">
                      {catalogStats.total_products || 0} products synced
                      {catalogStats.last_sync && (
                        <span className="ml-2">• Last sync: {new Date(catalogStats.last_sync).toLocaleString()}</span>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {suppliers.length > 0 && (
                      <Select 
                        value={suppliers[0]?.id || ""} 
                        onValueChange={(id) => handleSyncProducts(id)}
                        disabled={syncing}
                      >
                        <SelectTrigger className="w-64">
                          <SelectValue placeholder="Select supplier to sync" />
                        </SelectTrigger>
                        <SelectContent>
                          {suppliers.map((s) => (
                            <SelectItem key={s.id} value={s.id}>
                              Sync from {s.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    <Button 
                      onClick={() => handleSyncProducts(suppliers[0]?.id)} 
                      disabled={syncing || suppliers.length === 0}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? "animate-spin" : ""}`} />
                      {syncing ? "Syncing..." : "Sync Products"}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {/* Catalog Filters */}
                <div className="p-4 border-b bg-gray-50 flex items-center gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search products by name or SKU..."
                      value={catalogSearch}
                      onChange={(e) => setCatalogSearch(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <Select value={catalogFilters.category} onValueChange={(v) => setCatalogFilters({ ...catalogFilters, category: v })}>
                    <SelectTrigger className="w-44">
                      <SelectValue placeholder="Category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {(catalogStats.categories || []).map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={catalogFilters.stock_status} onValueChange={(v) => setCatalogFilters({ ...catalogFilters, stock_status: v })}>
                    <SelectTrigger className="w-36">
                      <SelectValue placeholder="Stock" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Stock</SelectItem>
                      <SelectItem value="instock">In Stock</SelectItem>
                      <SelectItem value="outofstock">Out of Stock</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Stats Cards */}
                {catalogStats.by_category && Object.keys(catalogStats.by_category).length > 0 && (
                  <div className="p-4 border-b bg-white">
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(catalogStats.by_category).map(([cat, count]) => (
                        <Badge 
                          key={cat} 
                          variant="outline" 
                          className="cursor-pointer hover:bg-blue-50"
                          onClick={() => setCatalogFilters({ ...catalogFilters, category: cat })}
                        >
                          {cat}: {count}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Products Table */}
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>Product</TableHead>
                      <TableHead>SKU</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Price</TableHead>
                      <TableHead>Stock</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {catalogProducts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                          <Globe className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                          <p>No products synced yet</p>
                          <p className="text-sm mt-1">Click "Sync Products" to fetch products from your supplier</p>
                        </TableCell>
                      </TableRow>
                    ) : (
                      catalogProducts.map((product) => (
                        <TableRow key={product.id} className="hover:bg-gray-50">
                          <TableCell>
                            <div className="flex items-center gap-3">
                              {product.image_url && (
                                <img src={product.image_url} alt={product.name} className="w-12 h-12 object-cover rounded" />
                              )}
                              <div>
                                <p className="font-medium text-gray-900 line-clamp-2">{product.name}</p>
                                {product.product_url && (
                                  <a href={product.product_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">
                                    View on supplier site →
                                  </a>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="font-mono">{product.sku}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">{product.category || "Uncategorized"}</Badge>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-bold text-green-600">${product.price?.toFixed(2)}</p>
                              {product.original_price > product.price && (
                                <p className="text-xs text-gray-400 line-through">${product.original_price?.toFixed(2)}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={product.stock_status === "instock" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}>
                              {product.stock_status === "instock" ? "In Stock" : "Out of Stock"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => {
                                // Pre-fill order item with this product
                                setOrderItems([{ 
                                  product_name: product.name, 
                                  sku: product.sku, 
                                  quantity: 1, 
                                  unit_cost: product.price 
                                }]);
                                setEditingOrder({
                                  supplier_id: product.supplier_id,
                                  shipping_cost: 0,
                                  customs_duty: 0,
                                  other_charges: 0,
                                  currency: "USD",
                                  exchange_rate: 280,
                                  notes: "",
                                });
                                setOrderDialog(true);
                                setActiveTab("orders");
                              }}
                            >
                              <Plus className="w-3 h-3 mr-1" /> Add to PO
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Suppliers Tab */}
          <TabsContent value="suppliers">
            <Card>
              <CardHeader className="border-b">
                <div className="flex items-center justify-between">
                  <CardTitle>Suppliers</CardTitle>
                  <Button onClick={() => { setEditingSupplier({ name: "", email: "", phone: "", website: "", address: "", country: "", payment_terms: "", notes: "" }); setSupplierDialog(true); }} className="bg-green-600 hover:bg-green-700">
                    <Plus className="w-4 h-4 mr-2" /> Add Supplier
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>Supplier</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Website</TableHead>
                      <TableHead>Payment Terms</TableHead>
                      <TableHead>Total Purchases</TableHead>
                      <TableHead>Outstanding</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {suppliers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                          <Building2 className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                          <p>No suppliers added yet</p>
                          <Button onClick={() => { setEditingSupplier({ name: "", email: "", phone: "", website: "", address: "", country: "", payment_terms: "", notes: "" }); setSupplierDialog(true); }} variant="link" className="mt-2">Add your first supplier</Button>
                        </TableCell>
                      </TableRow>
                    ) : (
                      suppliers.map((supplier) => (
                        <TableRow key={supplier.id} className="hover:bg-gray-50">
                          <TableCell>
                            <div>
                              <p className="font-medium text-gray-900">{supplier.name}</p>
                              {supplier.country && <p className="text-xs text-gray-500">{supplier.country}</p>}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              {supplier.email && (
                                <div className="flex items-center gap-1 text-sm text-gray-600">
                                  <Mail className="w-3 h-3" /> {supplier.email}
                                </div>
                              )}
                              {supplier.phone && (
                                <div className="flex items-center gap-1 text-sm text-gray-600">
                                  <Phone className="w-3 h-3" /> {supplier.phone}
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {supplier.website && (
                              <a href={supplier.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-600 hover:underline">
                                <Globe className="w-3 h-3" /> Visit
                              </a>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{supplier.payment_terms || "Not set"}</Badge>
                          </TableCell>
                          <TableCell className="font-medium text-gray-900">
                            ${(supplier.total_purchases || 0).toLocaleString()}
                          </TableCell>
                          <TableCell className={supplier.outstanding_balance > 0 ? "font-medium text-red-600" : "text-gray-500"}>
                            ${(supplier.outstanding_balance || 0).toLocaleString()}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="sm" onClick={() => { setEditingSupplier(supplier); setSupplierDialog(true); }}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteSupplier(supplier.id)}>
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Accounts Payable Tab */}
          <TabsContent value="payables">
            <Card>
              <CardHeader className="border-b">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Accounts Payable</CardTitle>
                    <p className="text-sm text-gray-500 mt-1">Track what you owe to suppliers</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">Total Outstanding</p>
                    <p className="text-3xl font-bold text-red-600">
                      ${(payablesSummary?.total_outstanding || 0).toLocaleString()}
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>Supplier</TableHead>
                      <TableHead>Orders</TableHead>
                      <TableHead>Total Amount</TableHead>
                      <TableHead>Paid</TableHead>
                      <TableHead>Outstanding</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {!payablesSummary?.payables?.length ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                          <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-400" />
                          <p>No outstanding payables!</p>
                        </TableCell>
                      </TableRow>
                    ) : (
                      payablesSummary.payables.map((payable) => (
                        <TableRow key={payable.supplier_id} className="hover:bg-gray-50">
                          <TableCell className="font-medium text-gray-900">{payable.supplier_name}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{payable.total_orders} orders</Badge>
                          </TableCell>
                          <TableCell className="font-medium">${payable.total_amount?.toLocaleString()}</TableCell>
                          <TableCell className="text-green-600">${payable.total_paid?.toLocaleString()}</TableCell>
                          <TableCell className="font-bold text-red-600">${payable.outstanding?.toLocaleString()}</TableCell>
                          <TableCell>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => setFilters({ ...filters, supplier_id: payable.supplier_id, status: "all", payment_status: "UNPAID" })}
                            >
                              View Orders
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Supplier Dialog */}
      <Dialog open={supplierDialog} onOpenChange={setSupplierDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingSupplier?.id ? "Edit Supplier" : "Add Supplier"}</DialogTitle>
          </DialogHeader>
          {editingSupplier && (
            <div className="grid gap-4 py-4">
              <div>
                <label className="text-sm font-medium">Supplier Name *</label>
                <Input
                  value={editingSupplier.name}
                  onChange={(e) => setEditingSupplier({ ...editingSupplier, name: e.target.value })}
                  placeholder="e.g., American Distributors LLC"
                  className="mt-1"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Email</label>
                  <Input
                    value={editingSupplier.email}
                    onChange={(e) => setEditingSupplier({ ...editingSupplier, email: e.target.value })}
                    placeholder="contact@supplier.com"
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Phone</label>
                  <Input
                    value={editingSupplier.phone}
                    onChange={(e) => setEditingSupplier({ ...editingSupplier, phone: e.target.value })}
                    placeholder="+1 (630) 422-1915"
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Website</label>
                <Input
                  value={editingSupplier.website}
                  onChange={(e) => setEditingSupplier({ ...editingSupplier, website: e.target.value })}
                  placeholder="https://americandistributorsllc.com"
                  className="mt-1"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Country</label>
                  <Input
                    value={editingSupplier.country}
                    onChange={(e) => setEditingSupplier({ ...editingSupplier, country: e.target.value })}
                    placeholder="USA"
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Payment Terms</label>
                  <Select
                    value={editingSupplier.payment_terms || ""}
                    onValueChange={(v) => setEditingSupplier({ ...editingSupplier, payment_terms: v })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select terms" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Prepaid">Prepaid</SelectItem>
                      <SelectItem value="Net 15">Net 15</SelectItem>
                      <SelectItem value="Net 30">Net 30</SelectItem>
                      <SelectItem value="Net 60">Net 60</SelectItem>
                      <SelectItem value="COD">COD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Address</label>
                <Textarea
                  value={editingSupplier.address}
                  onChange={(e) => setEditingSupplier({ ...editingSupplier, address: e.target.value })}
                  placeholder="Full address..."
                  rows={2}
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Notes</label>
                <Textarea
                  value={editingSupplier.notes}
                  onChange={(e) => setEditingSupplier({ ...editingSupplier, notes: e.target.value })}
                  placeholder="Additional notes..."
                  rows={2}
                  className="mt-1"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSupplierDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveSupplier} className="bg-green-600 hover:bg-green-700">
              <Save className="w-4 h-4 mr-2" /> Save Supplier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Purchase Order Dialog */}
      <Dialog open={orderDialog} onOpenChange={setOrderDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingOrder?.id ? `Edit Purchase Order - ${editingOrder.po_number}` : "New Purchase Order"}</DialogTitle>
            <DialogDescription>Create or edit a purchase order to track supplier purchases</DialogDescription>
          </DialogHeader>
          {editingOrder && (
            <div className="grid gap-6 py-4">
              {/* Supplier Selection */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Supplier *</label>
                  <Select
                    value={editingOrder.supplier_id}
                    onValueChange={(v) => setEditingOrder({ ...editingOrder, supplier_id: v })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select supplier" />
                    </SelectTrigger>
                    <SelectContent>
                      {suppliers.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Supplier Order/Invoice Ref</label>
                  <Input
                    value={editingOrder.supplier_order_ref || ""}
                    onChange={(e) => setEditingOrder({ ...editingOrder, supplier_order_ref: e.target.value })}
                    placeholder="e.g., INV-294246"
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Order Items */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium">Order Items</label>
                  <Button variant="outline" size="sm" onClick={addOrderItem}>
                    <Plus className="w-4 h-4 mr-1" /> Add Item
                  </Button>
                </div>
                <div className="border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="w-1/3">Product Name</TableHead>
                        <TableHead>SKU</TableHead>
                        <TableHead className="w-24">Qty</TableHead>
                        <TableHead className="w-32">Unit Cost ($)</TableHead>
                        <TableHead className="w-32">Total</TableHead>
                        <TableHead className="w-12"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {orderItems.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Input
                              value={item.product_name}
                              onChange={(e) => updateOrderItem(index, "product_name", e.target.value)}
                              placeholder="Product name"
                              className="h-8"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              value={item.sku || ""}
                              onChange={(e) => updateOrderItem(index, "sku", e.target.value)}
                              placeholder="SKU"
                              className="h-8"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              value={item.quantity}
                              onChange={(e) => updateOrderItem(index, "quantity", parseInt(e.target.value) || 0)}
                              min="1"
                              className="h-8"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              value={item.unit_cost}
                              onChange={(e) => updateOrderItem(index, "unit_cost", parseFloat(e.target.value) || 0)}
                              step="0.01"
                              min="0"
                              className="h-8"
                            />
                          </TableCell>
                          <TableCell className="font-medium">
                            ${(item.quantity * item.unit_cost).toFixed(2)}
                          </TableCell>
                          <TableCell>
                            <Button variant="ghost" size="sm" onClick={() => removeOrderItem(index)}>
                              <X className="w-4 h-4 text-red-500" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Costs */}
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <label className="text-sm font-medium">Shipping ($)</label>
                  <Input
                    type="number"
                    value={editingOrder.shipping_cost}
                    onChange={(e) => setEditingOrder({ ...editingOrder, shipping_cost: parseFloat(e.target.value) || 0 })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Customs Duty ($)</label>
                  <Input
                    type="number"
                    value={editingOrder.customs_duty}
                    onChange={(e) => setEditingOrder({ ...editingOrder, customs_duty: parseFloat(e.target.value) || 0 })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Other Charges ($)</label>
                  <Input
                    type="number"
                    value={editingOrder.other_charges}
                    onChange={(e) => setEditingOrder({ ...editingOrder, other_charges: parseFloat(e.target.value) || 0 })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Exchange Rate (to PKR)</label>
                  <Input
                    type="number"
                    value={editingOrder.exchange_rate}
                    onChange={(e) => setEditingOrder({ ...editingOrder, exchange_rate: parseFloat(e.target.value) || 1 })}
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Total Summary */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-sm text-gray-500">Subtotal</p>
                    <p className="text-xl font-bold">${subtotal.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total (USD)</p>
                    <p className="text-2xl font-bold text-green-600">${total.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total (PKR)</p>
                    <p className="text-2xl font-bold text-blue-600">Rs {(total * (editingOrder.exchange_rate || 1)).toLocaleString()}</p>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="text-sm font-medium">Notes</label>
                <Textarea
                  value={editingOrder.notes || ""}
                  onChange={(e) => setEditingOrder({ ...editingOrder, notes: e.target.value })}
                  placeholder="Additional notes about this order..."
                  rows={2}
                  className="mt-1"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOrderDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveOrder} className="bg-green-600 hover:bg-green-700">
              <Save className="w-4 h-4 mr-2" /> Save Purchase Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={paymentDialog} onOpenChange={setPaymentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
            <DialogDescription>
              {paymentOrder && `Recording payment for ${paymentOrder.po_number} - ${paymentOrder.supplier_name}`}
            </DialogDescription>
          </DialogHeader>
          {paymentOrder && (
            <div className="grid gap-4 py-4">
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500">Total Amount:</span>
                    <span className="ml-2 font-medium">${paymentOrder.total_amount?.toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Already Paid:</span>
                    <span className="ml-2 font-medium text-green-600">${paymentOrder.amount_paid?.toLocaleString()}</span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-gray-500">Outstanding:</span>
                    <span className="ml-2 font-bold text-red-600">
                      ${(paymentOrder.total_amount - (paymentOrder.amount_paid || 0)).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Payment Amount ($) *</label>
                <Input
                  type="number"
                  value={paymentData.amount}
                  onChange={(e) => setPaymentData({ ...paymentData, amount: parseFloat(e.target.value) || 0 })}
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Payment Method</label>
                <Select
                  value={paymentData.payment_method}
                  onValueChange={(v) => setPaymentData({ ...paymentData, payment_method: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                    <SelectItem value="Credit Card">Credit Card</SelectItem>
                    <SelectItem value="PayPal">PayPal</SelectItem>
                    <SelectItem value="Wire Transfer">Wire Transfer</SelectItem>
                    <SelectItem value="Cash">Cash</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Reference Number</label>
                <Input
                  value={paymentData.reference_number}
                  onChange={(e) => setPaymentData({ ...paymentData, reference_number: e.target.value })}
                  placeholder="Transaction/Check number"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Notes</label>
                <Textarea
                  value={paymentData.notes}
                  onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                  placeholder="Payment notes..."
                  rows={2}
                  className="mt-1"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentDialog(false)}>Cancel</Button>
            <Button onClick={handleRecordPayment} className="bg-green-600 hover:bg-green-700">
              <CreditCard className="w-4 h-4 mr-2" /> Record Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupplierPurchases;
